import moment from 'moment-timezone';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import styled from 'styled-components';

import '../../assets/stylesheets/molecules/datepicker.scss';
import { dateFormats } from '../../constants';
import { useFleet, useInput } from '../../hooks';
import CalendarIcon from '../../assets/images/icons/calendar.svg';
import ChevronRight from '../../assets/images/icons/chevron-right.svg';
import { colorScheme, zIndex } from '../../theme';
import { Flex } from './flex';
import { GhostButton } from './ghostButton';
import { Input } from './input';
import { Relative } from './relative';
import { SpaceBetween } from './spaceBetween';
import { WithClickOutside } from './withClickOutside';

interface DayRangePickerProps {
  startDate: Date;
  endDate: Date;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
  maxMonths?: number;
  minSelectableDate?: Date;
  maxSelectableDate?: Date;
}

interface Preset {
  label: string;
  getRange: (minDate: Date, maxDate: Date) => [Date, Date];
}

const clampDate = (date: Date, min: Date, max: Date): Date => {
  if (date < min) { return min; }
  if (date > max) { return max; }
  return date;
};

const PRESETS: Preset[] = [
  {
    label: 'Last 7 Days',
    getRange: (minDate, maxDate) => [clampDate(moment().subtract(7, 'days').startOf('day').toDate(), minDate, maxDate), maxDate],
  },
  {
    label: 'Last 30 Days',
    getRange: (minDate, maxDate) => [clampDate(moment().subtract(30, 'days').startOf('day').toDate(), minDate, maxDate), maxDate],
  },
  {
    label: 'Last 3 Months',
    getRange: (minDate, maxDate) => [clampDate(moment().subtract(3, 'months').startOf('day').toDate(), minDate, maxDate), maxDate],
  },
  {
    label: 'Last 6 Months',
    getRange: (minDate, maxDate) => [clampDate(moment().subtract(6, 'months').startOf('day').toDate(), minDate, maxDate), maxDate],
  },
] as const;

const TriggerButton = styled.button`
  min-width: 100px;
  height: 36px;
  padding: 8px 8px 8px 30px;
  font-size: 14px;
  font-weight: 400;
  color: ${colorScheme.input.text};
  background-color: ${colorScheme.input.background};
  border: 1px solid ${colorScheme.input.border};
  border-radius: 8px;
  appearance: none;
  background-image: url(${CalendarIcon});
  background-repeat: no-repeat;
  background-position: left 8px center;
  cursor: pointer;
  transition: all 150ms linear;

  &:focus {
    border-color: ${colorScheme.focus};
    box-shadow: none;
    outline: none;
  }
`;

const Popover = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: ${zIndex.modal};
  display: flex;
  background: ${colorScheme.panelBackground};
  border: 1px solid ${colorScheme.primaryBorder};
  border-top: 4px solid ${colorScheme.brand};
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  animation: fadeIn 150ms ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 0;
  border-right: 1px solid ${colorScheme.primaryBorder};
  min-width: 145px;
  background-color: ${colorScheme.buttons.secondary.hover};
`;

const PresetItem = styled.button<{ active?: boolean }>`
  background: ${(p) => (p.active ? colorScheme.card.background : 'transparent')};
  border: none;
  padding: 10px 20px;
  text-align: left;
  font-size: 14px;
  color: ${(p) => (p.active ? colorScheme.brand : colorScheme.text.base)};
  cursor: pointer;
  white-space: nowrap;
  transition: all 150ms linear;

  &:hover {
    background: ${colorScheme.card.background};
  }
`;

const CalendarSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  background: ${colorScheme.panelBackground};

  && .react-datepicker {
    display: flex !important;
    gap: 12px;
    justify-content: flex-start;
    align-items: flex-start;
    border: none;
    border-radius: 0;
    box-shadow: none;
    background: ${colorScheme.panelBackground};
    border-top: 1px solid ${colorScheme.primaryBorder};
    padding: 20px 10px;
  }

  && .react-datepicker__header {
    background: ${colorScheme.panelBackground};
    border-bottom: none;
    border-radius: 0 !important;
    text-align: left;
    padding: 0;
  }

  && .react-datepicker__current-month {
    margin-left: 5px;
  }

  && .react-datepicker__month-container {
    background: ${colorScheme.panelBackground} !important;
    border: none;
    margin: 0;
  }

  && .react-datepicker div[role='table'] {
    margin: 0;
  }

  .react-datepicker__day-names,
  .react-datepicker__week,
  .react-datepicker__month {
    background: ${colorScheme.panelBackground};
    margin: 0;
    border-radius: 0;
  }

  .react-datepicker__week {
    isolation: isolate;
  }

  && .react-datepicker__day,
  && .react-datepicker__day-name {
    width: 30px;
    height: 30px;
    line-height: 30px;
  }

  && .react-datepicker__day {
    transition: all 150ms linear;
  }

  .react-datepicker__day--outside-month {
    visibility: hidden;
    pointer-events: none;
    vertical-align: top;
  }

  .react-datepicker__week:not(
    :has(.react-datepicker__day:not(.react-datepicker__day--outside-month))
  ) {
    display: none;
  }

  .react-datepicker__day--disabled {
    opacity: 0.35;

    &:hover {
      background-color: transparent !important;
    }
  }

  .react-datepicker__day:hover,
  .react-datepicker__day--selected,
  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--in-range,
  .react-datepicker__day--range-start,
  .react-datepicker__day--range-end {
    border-radius: 50%;
  }

  .react-datepicker__day--keyboard-selected {
    background-color: transparent;
    color: inherit;

    &:focus-visible {
      border-color: ${colorScheme.buttons.primary.background};
      color: ${colorScheme.buttons.primary.text};
      border-radius: 50%;
    }
  }

  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--in-range {
    background-color: ${colorScheme.buttons.secondary.background};
  }

  .react-datepicker__day--range-start,
  .react-datepicker__day--range-end {
    background-color: ${colorScheme.buttons.primary.background};
    color: white;
  }

  .react-datepicker__day--in-range,
  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--range-start,
  .react-datepicker__day--range-end {
    position: relative;
  }

  /* Bridge highlight between consecutive in-range days. stops before row-end, range-end, and month boundaries */
  .react-datepicker__day--in-range:not(:last-child):not(.react-datepicker__day--range-end):not(
    :has(+ .react-datepicker__day--outside-month)
  )::after,
  .react-datepicker__day--range-start:not(:last-child):not(.react-datepicker__day--range-end):not(
    :has(+ .react-datepicker__day--outside-month)
  )::after {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    width: 130%;
    height: 100%;
    background-color: ${colorScheme.buttons.secondary.background};
    z-index: -${zIndex.base};
  }

  .react-datepicker__day--range-end:not(:first-child):not(.react-datepicker__day--range-start)::before {
    content: '';
    position: absolute;
    top: 0;
    right: 50%;
    width: 130%;
    height: 100%;
    background-color: ${colorScheme.buttons.secondary.background};
    z-index: -${zIndex.base};
  }

  && .react-datepicker__navigation--previous,
  && .react-datepicker__navigation--next {
    background-image: url(${ChevronRight});
    width: 24px;
    height: 24px;
    transition: all 150ms linear;
  }

  && .react-datepicker__navigation--previous {
    left: -15px;
    transform: rotate(180deg);
  }

  && .react-datepicker__navigation--next {
    right: -15px;
    transform: none;
  }
`;


const InputGroup = styled.div`
  flex: 1;
`;

const InputGroupLabel = styled.span`
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: ${colorScheme.text.base};
  margin-bottom: 4px;
`;

const InputHint = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${colorScheme.text.muted};
  margin-left: 4px;
`;


export const DayRangePicker: React.FC<DayRangePickerProps> = ({
  startDate, endDate, onStartDateChange, onEndDateChange, maxMonths, minSelectableDate, maxSelectableDate,
}) => {
  const fleet = useFleet();
  // The literal pattern behind `dateFormats.date` — "MM/DD/YYYY" in English, "DD.MM.YYYY"
  // in Polish — so the hint and placeholder tell the user which order to type.
  const datePattern = moment.localeData().longDateFormat('L');
  const [isOpen, setIsOpen] = useState(false);
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const [tempStart, setTempStart] = useState<Date | null>(startDate);
  const [tempEnd, setTempEnd] = useState<Date | null>(endDate);
  const [calendarKey, setCalendarKey] = useState(0);
  const initialStart = useRef(startDate);
  const initialEnd = useRef(endDate);
  const startText = useInput(moment(startDate).format(dateFormats.date));
  const endText = useInput(moment(endDate).format(dateFormats.date));

  const fleetCreatedAt = useMemo(() => new Date(fleet.created_at || Date.now()), [fleet.created_at]);
  const hardMin = useMemo(() => minSelectableDate ?? fleetCreatedAt, [minSelectableDate, fleetCreatedAt]);
  const hardMax = useMemo(() => maxSelectableDate ?? moment().subtract(1, 'day').endOf('day').toDate(), [maxSelectableDate]);

  useEffect(() => {
    setTempStart(startDate);
    setTempEnd(endDate);
    startText.setValue(moment(startDate).format(dateFormats.date));
    endText.setValue(moment(endDate).format(dateFormats.date));
  }, [startDate, endDate]);

  const handleRangeChange = useCallback(
    (dates: [Date | null, Date | null]) => {
      const [start, end] = dates;
      setTempStart(start);
      setTempEnd(end);
      if (start) { startText.setValue(moment(start).format(dateFormats.date)); }
      if (end) { endText.setValue(moment(end).format(dateFormats.date)); }
      if (start && end) {
        onStartDateChange(moment(start).startOf('day').toDate());
        onEndDateChange(moment(end).endOf('day').toDate());
        setActivePreset('Custom');
      }
    },
    [onStartDateChange, onEndDateChange],
  );

  const minDate = useMemo(() => {
    if (maxMonths == null) {
      return hardMin;
    }
    const limitedMin = moment(endDate).subtract(maxMonths, 'months').startOf('day').toDate();
    return limitedMin > hardMin ? limitedMin : hardMin;
  }, [hardMin, endDate, maxMonths]);

  const handleDateInput = useCallback(
    (text: string, field: 'start' | 'end') => {
      const parsed = moment(text, dateFormats.date, true);
      if (!parsed.isValid()) { return; }
      let date = parsed.toDate();
      if (field === 'start') {
        if (date < minDate) { date = minDate; }
        if (tempEnd && date > tempEnd) { date = tempEnd; }
      } else {
        if (date > hardMax) { date = hardMax; }
        if (tempStart && date < tempStart) { date = tempStart; }
      }
      const textInput = field === 'start' ? startText : endText;
      textInput.setValue(moment(date).format(dateFormats.date));
      if (field === 'start') {
        setTempStart(date);
        onStartDateChange(moment(date).startOf('day').toDate());
      } else {
        setTempEnd(date);
        onEndDateChange(moment(date).endOf('day').toDate());
      }
      setActivePreset('Custom');
      setCalendarKey((k) => k + 1);
    },
    [onStartDateChange, onEndDateChange, minDate, hardMax, startText, endText, tempStart, tempEnd],
  );

  const handleDateChange = useCallback(
    (field: 'start' | 'end') => (e: React.ChangeEvent<HTMLInputElement>) => {
      const textInput = field === 'start' ? startText : endText;
      textInput.bind.onChange(e);
      handleDateInput(e.target.value, field);
    },
    [startText, endText, handleDateInput],
  );

  const handleDateBlur = useCallback(
    (field: 'start' | 'end') => () => {
      const source = field === 'start' ? tempStart : tempEnd;
      const textInput = field === 'start' ? startText : endText;
      if (source) { textInput.setValue(moment(source).format(dateFormats.date)); }
    },
    [tempStart, tempEnd],
  );

  const applyPreset = useCallback(
    (preset: Preset) => {
      const [start, end] = preset.getRange(hardMin, hardMax);
      onStartDateChange(start);
      onEndDateChange(end);
      setActivePreset(preset.label);
    },
    [onStartDateChange, onEndDateChange, hardMin, hardMax],
  );

  const handleClear = useCallback(() => {
    const start = initialStart.current;
    const end = initialEnd.current;
    setTempStart(start);
    setTempEnd(end);
    startText.setValue(moment(start).format(dateFormats.date));
    endText.setValue(moment(end).format(dateFormats.date));
    onStartDateChange(start);
    onEndDateChange(end);
    setActivePreset(null);
    setCalendarKey((k) => k + 1);
  }, [onStartDateChange, onEndDateChange]);

  const triggerLabel = useMemo(() => {
    const start = moment(startDate).format(dateFormats.dateMedium);
    const end = moment(endDate).format(dateFormats.dateMedium);
    return `${start} — ${end}`;
  }, [startDate, endDate]);

  const effectiveMaxDate = useMemo(() => {
    if (maxMonths == null) {
      return hardMax;
    }
    const limitedMax = moment(startDate).add(maxMonths, 'months').endOf('day').toDate();
    return limitedMax < hardMax ? limitedMax : hardMax;
  }, [startDate, maxMonths, hardMax]);

  return (
    <WithClickOutside onClickOutside={() => setIsOpen(false)}>
      <Relative>
        <TriggerButton type="button" onClick={() => setIsOpen(!isOpen)}>{triggerLabel}</TriggerButton>

        {isOpen && (
          <Popover>
            <Sidebar>
              {PRESETS.map((preset) => (
                <PresetItem key={preset.label} type="button" active={activePreset === preset.label} onClick={() => applyPreset(preset)}>
                  {preset.label}
                </PresetItem>
              ))}
              <PresetItem type="button" active={activePreset === 'Custom'} onClick={() => setActivePreset('Custom')}>
                Custom
              </PresetItem>
            </Sidebar>

            <CalendarSection>
              <Flex gapSize="md" style={{ marginBottom: 12 }}>
                <InputGroup>
                  <InputGroupLabel>Start Date <InputHint>{datePattern}</InputHint></InputGroupLabel>
                  <Input
                    aria-label="Start date"
                    {...startText.bind}
                    onChange={handleDateChange('start')}
                    onBlur={handleDateBlur('start')}
                    placeholder={datePattern}
                    style={{ backgroundColor: colorScheme.panelBackground, height: 36 }}
                  />
                </InputGroup>
                <InputGroup>
                  <InputGroupLabel>End Date <InputHint>{datePattern}</InputHint></InputGroupLabel>
                  <Input
                    aria-label="End date"
                    {...endText.bind}
                    onChange={handleDateChange('end')}
                    onBlur={handleDateBlur('end')}
                    placeholder={datePattern}
                    style={{ backgroundColor: colorScheme.panelBackground, height: 36 }}
                  />
                </InputGroup>
              </Flex>

              <DatePicker
                key={calendarKey}
                selectsRange
                startDate={tempStart}
                endDate={tempEnd}
                onChange={handleRangeChange}
                openToDate={tempStart ?? undefined}
                monthsShown={2}
                inline
                minDate={minDate}
                maxDate={effectiveMaxDate}
              />

              <SpaceBetween style={{ paddingTop: '8px', borderTop: `1px solid ${colorScheme.primaryBorder}` }}>
                <GhostButton 
                  type="button" 
                  onClick={handleClear} 
                  style={{
                    border: 'none', 
                    fontSize: '14px', 
                    color: colorScheme.text.base
                  }}
                >
                  Clear
                </GhostButton>
                <GhostButton 
                  type="button" 
                  onClick={() => setIsOpen(false)} 
                  style={{
                    border: 'none', 
                    fontSize: '14px', 
                    color: colorScheme.text.base
                  }}
                >
                  Cancel
                </GhostButton>
              </SpaceBetween>
            </CalendarSection>
          </Popover>
        )}
      </Relative>
    </WithClickOutside>
  );
};
